/**
 * Utilitaires pour nettoyer et normaliser les URLs
 * Corrige les problèmes d'empilement de préfixes langue et de slugs "sales"
 */

export type LanguageCode = 'fr' | 'en' | 'es';

/**
 * Nettoie un path en supprimant tous les préfixes de langue successifs
 */
export const cleanLanguagePrefixes = (path: string): string => {
  // Supprimer tous les préfixes langue successifs en début d'URL
  return path.replace(/^\/?((?:fr|en|es)\/?)+(.*?)$/, '/$2')
             .replace(/\/+/g, '/') // Remplacer les double slashes
             .replace(/\/$/, '') || '/'; // Supprimer le slash final sauf pour la racine
};

/**
 * Normalise un slug en ne gardant que des caractères alphanumériques et des tirets
 * CRITIQUE : Gère tous les caractères spéciaux pour URLs SEO-safe
 */
export const normalizeSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    // Normaliser les accents et caractères spéciaux
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Retirer les accents
    // Remplacer les caractères spéciaux courants par des espaces d'abord
    .replace(/[':().,|/\\[\]{}+=*&^%$#@!?<>"`~;]/g, ' ')
    .replace(/\s+/g, '-') // Espaces et underscore vers tirets
    .replace(/_+/g, '-')
    // Garder seulement a-z, 0-9 et tirets
    .replace(/[^a-z0-9-]/g, '')
    .replace(/--+/g, '-') // Supprimer les tirets multiples
    .replace(/^-+|-+$/g, ''); // Supprimer les tirets en début/fin
};

/**
 * Extrait le préfixe de langue d'un path
 */
export const extractLanguagePrefix = (path: string): LanguageCode | null => {
  const match = path.match(/^\/?(fr|en|es)(?:\/|$)/);
  return match ? match[1] as LanguageCode : null;
};

/**
 * Construit une URL propre avec le préfixe de langue approprié
 */
export const buildCleanUrl = (path: string, language: LanguageCode): string => {
  const cleanPath = cleanLanguagePrefixes(path);
  
  if (language === 'fr') {
    // Français : pas de préfixe
    return cleanPath === '/' ? '/' : cleanPath;
  }
  
  // Anglais/Espagnol : ajouter le préfixe
  return `/${language}${cleanPath === '/' ? '' : cleanPath}`;
};

/**
 * Corrige les routes /games/ vers /game/
 */
export const fixGameRoute = (path: string): string => {
  return path.replace(/\/games\//g, '/game/');
};

/**
 * Détecte si une URL est une page métier qui doit être indexée
 */
export const isBusinessPage = (path: string): boolean => {
  const cleanPath = cleanLanguagePrefixes(path);
  
  // Pages qui NE doivent PAS être indexées
  const noIndexPatterns = [
    /^\/admin/,
    /^\/login/,
    /^\/register/,
    /^\/reset-password/,
    /^\/search/,
    /^\/auth/,
    /^\/api/,
    /^\/seo-audit/,
    /^\/arcade-spots-management/
  ];
  
  return !noIndexPatterns.some(pattern => pattern.test(cleanPath));
};

/**
 * Génère des URLs hreflang cohérentes
 */
export const generateHreflangUrls = (path: string, baseUrl: string = 'https://rankserveur.com'): Array<{hreflang: string, href: string}> => {
  // Utiliser normalizePathForSEO au lieu de cleanLanguagePrefixes + fixGameRoute
  const cleanPath = normalizePathForSEO(path);
  
  return [
    { 
      hreflang: 'fr-FR', 
      href: `${baseUrl}${cleanPath === '/' ? '' : cleanPath}` 
    },
    { 
      hreflang: 'en-US', 
      href: `${baseUrl}/en${cleanPath === '/' ? '' : cleanPath}` 
    },
    { 
      hreflang: 'es-ES', 
      href: `${baseUrl}/es${cleanPath === '/' ? '' : cleanPath}` 
    },
    { 
      hreflang: 'x-default', 
      href: `${baseUrl}${cleanPath === '/' ? '' : cleanPath}` 
    }
  ];
};

/**
 * Normalise un path pour le SEO en :
 * - Supprimant les doubles préfixes de langue (/en/en/ → /en/)
 * - Convertissant /games/ → /game/
 * - Nettoyant les slashes multiples
 * 
 * FONCTION CRITIQUE pour canonical et hreflang
 */
export const normalizePathForSEO = (path: string): string => {
  let normalized = path;
  
  // 1. Nettoyer les slashes multiples
  normalized = normalized.replace(/\/+/g, '/');
  
  // 2. Supprimer les doubles/triples préfixes de langue
  // Ex: /en/en/game/minecraft → /en/game/minecraft
  const langPrefixPattern = /^\/(fr|en|es)(?:\/(fr|en|es))+/;
  const match = normalized.match(langPrefixPattern);
  if (match) {
    // Extraire toutes les langues trouvées
    const langs = normalized.match(/^\/(fr|en|es)/g);
    if (langs && langs.length > 1) {
      // Garder seulement le premier préfixe langue valide
      const firstLang = langs[0]; // Ex: '/en'
      normalized = normalized.replace(langPrefixPattern, firstLang);
    }
  }
  
  // 3. CRITIQUE : Convertir /games/ → /game/
  normalized = normalized.replace(/\/games\//g, '/game/');
  
  // 4. Nettoyer à nouveau les slashes multiples après transformations
  normalized = normalized.replace(/\/+/g, '/');
  
  // 5. Supprimer le slash final sauf pour la racine
  if (normalized !== '/') {
    normalized = normalized.replace(/\/$/, '');
  }
  
  return normalized;
};

/**
 * Génère une URL canonique propre
 */
export const generateCanonicalUrl = (path: string, language: LanguageCode, baseUrl: string = 'https://rankserveur.com'): string => {
  // Utiliser la nouvelle fonction de normalisation complète
  const normalizedPath = normalizePathForSEO(path);
  const urlPath = buildCleanUrl(normalizedPath, language);
  
  return `${baseUrl}${urlPath === '/' ? '' : urlPath}`;
};